import React from 'react';
import { Input } from '../ui/input';
import { Search, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export type SortOption = 
  | 'name-asc' 
  | 'name-desc' 
  | 'sync-newest' 
  | 'sync-oldest' 
  | 'recently-added' 
  | 'sync-status';

interface SearchSortToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  totalCount: number;
  filteredCount: number;
}

export const SearchSortToolbar: React.FC<SearchSortToolbarProps> = ({
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
  totalCount,
  filteredCount,
}) => {
  return (
    <div className="mb-6 flex flex-col sm:flex-row gap-4 bg-gray-100 dark:bg-gray-800/50 p-4 rounded-lg">
      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-4 flex-1">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-10"
            aria-label="Search companies"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort Dropdown */}
        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger className="w-full sm:w-[220px]" aria-label="Sort companies">
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Name (A-Z)</SelectItem>
            <SelectItem value="name-desc">Name (Z-A)</SelectItem>
            <SelectItem value="sync-newest">Last Sync (Newest)</SelectItem>
            <SelectItem value="sync-oldest">Last Sync (Oldest)</SelectItem>
            <SelectItem value="recently-added">Recently Added</SelectItem>
            <SelectItem value="sync-status">Sync Enabled First</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Results Counter */}
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-4">
        {searchTerm ? (
          <span>
            Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredCount}</span> of{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span> companies
          </span>
        ) : (
          <span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span>{' '}
            {totalCount === 1 ? 'company' : 'companies'}
          </span>
        )}
      </div>
    </div>
  );
};

export default SearchSortToolbar;